/**
 * Feature E09-F06 — "Миграция исторических данных (parallel-run)" contracts.
 *
 * Toolkit for the one-time/repeatable migration of the LIVE EER app (legacy
 * Railway Postgres, fed by n8n + PHP) into the v2 stack (Fastify + Prisma on a
 * SEPARATE target Postgres). This is a 1:1 STRUCTURAL clone — the new Prisma
 * schema is a clone of the legacy `schema.sql`, so most tables move byte-for-byte
 * (`as-is`); only a handful need a transform (passwords, company import,
 * normalization). The whole thing runs in PARALLEL-RUN: old + new both live until
 * a final delta-sync + cut-over.
 *
 * Source of truth for the legacy structure / invariants:
 *   - "EER app/schema.sql"  (DDL + FK + seed plaintext passwords)
 *   - docs/source-n8n/eer-live-schema.md  (prod live column DDL, drift-checked)
 *   - docs/source-n8n/integrations.md     (Google Drive master-file source)
 *   - docs/source-n8n/ai_prompts.md       (canonical ai_prompts bodies)
 *
 * Lives in its OWN file (NOT re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 *
 * Endpoints (all admin-only — `app.authenticate` + role guard in the route):
 *
 *   GET  /admin/migration/plan
 *        → { ok, tables[], loadOrder[], asIsCount, transformCount }       (T01)
 *   POST /admin/migration/transform-passwords  { users[] }
 *        → { ok, transformed[], summary }                                 (T02)
 *   POST /admin/migration/verify-fk            { counts, rows }
 *        → { ok, violations[], rowCountDeltas[], passed }                 (T03)
 *   POST /admin/migration/import-companies     { rows[] }
 *        → { ok, imported, skipped, merged, rows[] }                      (T06)
 *   GET  /admin/migration/cutover-runbook
 *        → { ok, runbook }                                                (T05)
 */
import { z } from 'zod';
// ===========================================================================
// T01 — export plan + transform mapping
// ===========================================================================
/** How a legacy table is carried into v2. */
export const MIGRATION_TABLE_MODES = ['as-is', 'transform'];
export const migrationTableModeSchema = z.enum(MIGRATION_TABLE_MODES);
/** One legacy table in the migration plan. */
export const migrationTablePlanSchema = z.object({
    table: z.string(),
    /** `as-is` = byte-for-byte copy; `transform` = needs a per-row transform. */
    mode: migrationTableModeSchema,
    /** Tables this one references (FK) — drives the safe load order. */
    dependsOn: z.array(z.string()),
    /** Human note: what (if anything) is transformed, or why it is as-is. */
    note: z.string(),
});
export const migrationPlanResponseSchema = z.object({
    ok: z.literal(true),
    /** Every legacy table, classified. */
    tables: z.array(migrationTablePlanSchema),
    /** FK-safe topological load order (parents before children). */
    loadOrder: z.array(z.string()),
    asIsCount: z.number().int().nonnegative(),
    transformCount: z.number().int().nonnegative(),
});
// ===========================================================================
// T02 — users.password → argon2 | reset flag
// ===========================================================================
/** A legacy `users` row as it arrives from the dump (password may be plaintext). */
export const legacyUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    /** Stored value from the dump: plaintext ('123456') OR a hash OR null. */
    password: z.string().nullable().optional(),
});
export const transformPasswordsRequestSchema = z.object({
    users: z.array(legacyUserSchema).min(1),
});
/** What happened to one user's credential during the transform. */
export const PASSWORD_TRANSFORM_OUTCOMES = ['rehashed', 'reset-required'];
export const passwordTransformOutcomeSchema = z.enum(PASSWORD_TRANSFORM_OUTCOMES);
export const transformedUserSchema = z.object({
    id: z.string(),
    outcome: passwordTransformOutcomeSchema,
    /** argon2id PHC string when `outcome=rehashed`; null when `reset-required`. */
    passwordHash: z.string().nullable(),
    /** Mirrors users.password_reset_required (true when `outcome=reset-required`). */
    passwordResetRequired: z.boolean(),
});
export const transformPasswordsResponseSchema = z.object({
    ok: z.literal(true),
    transformed: z.array(transformedUserSchema),
    summary: z.object({
        total: z.number().int().nonnegative(),
        rehashed: z.number().int().nonnegative(),
        resetRequired: z.number().int().nonnegative(),
        /** MUST be 0 — invariant: no plaintext survives the transform. */
        plaintextRemaining: z.number().int().nonnegative(),
    }),
});
// ===========================================================================
// T03 — restore + FK verification
// ===========================================================================
/** Per-table source vs target row counts (with intentionally-dropped). */
export const rowCountPairSchema = z.object({
    table: z.string(),
    source: z.number().int().nonnegative(),
    target: z.number().int().nonnegative(),
    /** Rows intentionally NOT carried (e.g. expired sessions). */
    intentionallyDropped: z.number().int().nonnegative().default(0),
});
/** A foreign-key edge to validate (child.column → parent). */
export const fkEdgeSchema = z.object({
    /** Logical name e.g. 'sessions.user_id->users'. */
    name: z.string(),
    childTable: z.string(),
    childColumn: z.string(),
    parentTable: z.string(),
    /** ON DELETE behaviour (cascade | set null) — informational. */
    onDelete: z.enum(['cascade', 'set-null', 'restrict']),
    /** True when the FK column is nullable (a NULL is NOT a violation). */
    nullable: z.boolean(),
});
/** Child rows projected as { childId, fkValue } for one edge, plus parent ids. */
export const fkProbeSchema = z.object({
    name: z.string(),
    /** Non-null fk values present on child rows. */
    childFkValues: z.array(z.union([z.string(), z.number()])),
    /** The set of existing parent primary-key values. */
    parentIds: z.array(z.union([z.string(), z.number()])),
});
export const verifyFkRequestSchema = z.object({
    counts: z.array(rowCountPairSchema).default([]),
    probes: z.array(fkProbeSchema).default([]),
});
export const fkViolationSchema = z.object({
    edge: z.string(),
    /** fk values on child rows that point at a missing parent. */
    orphanValues: z.array(z.union([z.string(), z.number()])),
});
export const rowCountDeltaSchema = z.object({
    table: z.string(),
    source: z.number().int(),
    target: z.number().int(),
    /** target - (source - intentionallyDropped); 0 means reconciled. */
    delta: z.number().int(),
    reconciled: z.boolean(),
});
export const verifyFkResponseSchema = z.object({
    ok: z.literal(true),
    violations: z.array(fkViolationSchema),
    rowCountDeltas: z.array(rowCountDeltaSchema),
    /** true ⇔ no FK violations AND every table reconciled. */
    passed: z.boolean(),
});
// ===========================================================================
// T06 — Google Drive company master-file import → companies
// ===========================================================================
/** One raw company record from the Google Drive master file. */
export const companyImportRowSchema = z.object({
    name: z.string().min(1),
    domain: z.string().nullable().optional(),
    industry: z.string().nullable().optional(),
    subIndustry: z.string().nullable().optional(),
    sizeBand: z.string().nullable().optional(),
    hqLocation: z.string().nullable().optional(),
    linkedinUrl: z.string().nullable().optional(),
    /** Free-form extra fields preserved into companies.firmographics. */
    firmographics: z.record(z.string(), z.unknown()).optional(),
});
export const importCompaniesRequestSchema = z.object({
    rows: z.array(companyImportRowSchema).min(1),
    /** Provenance written to companies.source. */
    source: z.string().default('gdrive-master-file'),
});
export const COMPANY_IMPORT_OUTCOMES = ['inserted', 'merged', 'skipped'];
export const companyImportOutcomeSchema = z.enum(COMPANY_IMPORT_OUTCOMES);
export const importedCompanySchema = z.object({
    canonicalName: z.string(),
    normalizedName: z.string(),
    domain: z.string().nullable(),
    outcome: companyImportOutcomeSchema,
});
export const importCompaniesResponseSchema = z.object({
    ok: z.literal(true),
    imported: z.number().int().nonnegative(),
    merged: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    rows: z.array(importedCompanySchema),
});
// ===========================================================================
// T05 — cut-over runbook
// ===========================================================================
export const cutoverStepSchema = z.object({
    order: z.number().int().positive(),
    title: z.string(),
    detail: z.string(),
    /** When this step is reversible, the rollback action. */
    rollback: z.string().nullable(),
});
export const cutoverRunbookSchema = z.object({
    /** Final delta-sync before flipping traffic. */
    steps: z.array(cutoverStepSchema),
    /** Plain-list rollback plan (old stack restore). */
    rollbackPlan: z.array(z.string()),
});
export const cutoverRunbookResponseSchema = z.object({
    ok: z.literal(true),
    runbook: cutoverRunbookSchema,
});
//# sourceMappingURL=data-migration.js.map